import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@jsverse/transloco';
import { AssessmentReviewStatus } from '../../constants/assessment.constants';
import { GeneratedContent, RiskProfileData } from '../../models/document.model';
import { formatDate } from '../../utils/date.utils';
import { stripInlineMarkdown } from '../../utils/markdown.utils';
import { getReviewStatusLabel } from '../../utils/assessment.utils';
import { AiLabelComponent } from '../ai-label/ai-label';
import { EmptyStateComponent } from '../empty-state/empty-state';
import { StatusIndicatorComponent } from '../status-indicator/status-indicator';
import { TypeIconComponent } from '../type-icon/type-icon';

type ModelCardSummaryVariant = 'report' | 'panel';

@Component({
  selector: 'app-model-card-summary',
  imports: [
    AiLabelComponent,
    MatIconModule,
    TranslocoModule,
    EmptyStateComponent,
    StatusIndicatorComponent,
    TypeIconComponent,
  ],
  templateUrl: './model-card-summary.html',
  styleUrl: './model-card-summary.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModelCardSummaryComponent {
  /** `report` — the edit-assessment Model Card tab (no export footer).
   *  `panel` — embedded in the assessment detail drawer; flush width, keeps
   *  an export footer. Both render the same plain-language overview. */
  variant = input<ModelCardSummaryVariant>('report');
  riskProfile = input<RiskProfileData | null>(null);
  /** Report-facing generated copy (summary / what-it-does / deployment /
   *  components / assessed date). Lives on the assessment's
   *  `generated_content`, separate from `risk_profile`. */
  generatedContent = input<GeneratedContent | null>(null);
  assessmentId = input<string | null>(null);
  assessmentName = input('Assessment');
  assessmentDescription = input<string | undefined>(undefined);
  preparedDate = input<string | null>(null);
  reviewStatus = input<AssessmentReviewStatus | null>(null);
  canExport = input(false);
  exportTooltip = input('');
  isExporting = input(false);

  exportRequested = output<void>();

  readonly getReviewStatusLabel = getReviewStatusLabel;
  readonly AssessmentReviewStatus = AssessmentReviewStatus;

  /** Overview "Assessed" date — prefers the generated `assessment_date`,
   *  falling back to the record's prepared/updated date. */
  readonly assessedDate = computed(() =>
    formatDate(this.generatedContent()?.assessment_date ?? this.preparedDate(), 'long')
  );

  readonly reportReference = computed(() => {
    const id = this.assessmentId()?.trim();
    return id || null;
  });

  /** Overview summary line — the redesign's `summary_description`, falling
   *  back to the assessment description for legacy records that predate it.
   *  Stripped of inline markdown emphasis (BE copy sometimes carries `**…**`)
   *  and clamped in CSS so a long legacy summary doesn't wall the header. */
  readonly summaryText = computed(() =>
    stripInlineMarkdown(
      this.generatedContent()?.summary_description?.trim() ||
        this.assessmentDescription()?.trim() ||
        ''
    )
  );

  /** Whether any of the overview meta cells (deployment / components / date)
   *  have content to render — drives the meta row's visibility. */
  readonly hasOverviewMeta = computed(() => {
    const content = this.generatedContent();
    return !!(content?.deployment?.trim() || content?.ai_components?.trim() || this.assessedDate());
  });

  /** "What it does" split into paragraphs on blank lines so multi-paragraph
   *  copy renders with spacing rather than one run-on block. Only the curated
   *  `what_it_does` field — legacy records without it hide the section. */
  readonly whatItDoesParagraphs = computed(() =>
    stripInlineMarkdown(this.generatedContent()?.what_it_does ?? '')
      .split(/\n\s*\n/)
      .map(p => p.trim())
      .filter(p => p.length > 0)
  );

  onExportClick(): void {
    if (!this.canExport() || this.isExporting()) return;
    this.exportRequested.emit();
  }
}
